import { osloDateString } from "@/lib/oslo-time";
import type { CustomerActivityType, DBCustomerActivity } from "@/lib/db-types";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Er kunden kontaktet de siste `days` dagene? Grensen er eksklusiv: ved
 * nøyaktig `days` dager siden regnes kunden ikke lenger som nylig kontaktet
 * (terskelen, 14 dager, bor i system_config.crm_recent_contact_days).
 */
export function isRecentlyContacted(
  lastContactAt: string | Date | null | undefined,
  now: Date,
  days: number,
): boolean {
  if (!lastContactAt) return false;
  const last = lastContactAt instanceof Date ? lastContactAt : new Date(lastContactAt);
  if (Number.isNaN(last.getTime())) return false;
  return now.getTime() - last.getTime() < days * DAY_MS;
}

export interface ActivityDayGroup<T extends Pick<DBCustomerActivity, "occurred_at">> {
  /** YYYY-MM-DD i Europe/Oslo. */
  date: string;
  items: T[];
}

/** Grupperer per kalenderdag i Oslo-tid, nyeste dag først, rekkefølgen innen dagen beholdes. */
export function groupActivitiesByDay<T extends Pick<DBCustomerActivity, "occurred_at">>(
  list: readonly T[],
): ActivityDayGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of list) {
    const date = osloDateString(item.occurred_at);
    const bucket = groups.get(date);
    if (bucket) bucket.push(item);
    else groups.set(date, [item]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, items]) => ({ date, items }));
}

const ACTIVITY_TYPE_LABELS: Record<CustomerActivityType, string> = {
  email_out: "E-post ut",
  email_in: "E-post inn",
  call: "Samtale",
  sms_out: "SMS ut",
  meeting: "Møte",
  note: "Notat",
  summary: "Referat",
};

export function activityTypeLabel(type: CustomerActivityType): string {
  return ACTIVITY_TYPE_LABELS[type] ?? type;
}

/**
 * Skjuler sitert tekst i visning av innkommende e-post («On ... wrote:», «Den ... skrev ...:»
 * og linjer som starter med «>»). Teksten lagres alltid i full; dette er kun forhåndsvisning.
 */
export function stripQuotedReply(text: string): string {
  const lines = text.split(/\r?\n/);
  const cut = lines.findIndex(
    (l) => /^(On .* wrote:|Den .* skrev .*:)\s*$/.test(l.trim()) || l.startsWith(">"),
  );
  return (cut === -1 ? lines : lines.slice(0, cut)).join("\n").trim();
}
